<template>
  <div
    :id="commentAreaId"
    class="pega-comment-area"
    :class="[{'pega-comment-area-boss': jobComment.isCompanyStaff }, { 'pega-comment-area-adm': !jobComment.isCompanyStaff }]"
    data-test="comment-area"
    @click.stop
    @select.stop
  >
    <div
      class="boss-icon-cross pega-comment-delete-icon"
      data-test="delete-icon"
      @click="deleteComment"
    />
    <div
      :class="{'is-open': status.isOpen}"
      data-test="comment-accordion"
    >
      <div
        class="boss-mini-accordion-head pega-comment-header"
        data-test="title"
        @click="toggle"
      >
        「{{ jobComment.text }}」へのコメント
      </div>
      <div class="boss-mini-accordion-body pega-comment-body">
        <template v-if="getClComment">
          <span data-test="cl-header"> 【{{ !isCompanyStaffAccess | businessesPartnerName }}コメント】</span><br>
          <span
            class="pega-comment-update-date"
            data-test="cl-update"
          >
            最終更新者：{{ getClComment.author }}{{ clCommentHonorific }}（{{ getClComment.lastUpdateDate | formatDate }}）
          </span><br>
          <div class="pega-comment">
            <span
              class="pega-pre-wrap"
              data-test="cl-comment"
              v-text="getClComment.comment"
            />
          </div>
          <hr>
        </template>
        <span data-test="my-header">【{{ isCompanyStaffAccess | businessesPartnerName }}コメント】</span><br>
        <span
          v-if="getMyComment"
          class="pega-comment-update-date"
          data-test="my-update"
        >
          最終更新者：{{ getMyComment.author }}{{ myCommentHonorific }}（{{ getMyComment.lastUpdateDate | formatDate }}）
        </span>
        <textarea
          v-if="isEdit"
          v-model="getMyComment.comment"
          class="pega-comment-from-writer"
          :class="{'pega-comment-from-writer-pending':getMyComment.isPending}"
          data-test="my-comment"
          @change="$emit('changeComment',jobComment)"
        />
        <div
          v-else
          class="pega-comment"
        >
          <span
            class="pega-pre-wrap"
            :class="{'pega-comment-content':!getMyComment.comment}"
            data-test="my-comment-preview"
            v-text="getMyComment.comment || '［「編集する」を押して、コメントを入力してください］'"
          />
        </div>
        <button
          v-if="!isEdit"
          class="float-right boss-button pega-comment-edit-button"
          data-test="edit-button"
          @click="changeEdit"
        >
          編集する
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { createDate } from '@/lib/dateUtils.js'
import jobPreviewCommon from '@/lib/jobPreviewCommon'

export default {
  filters: {
    formatDate (val, defaultLabel = '----/--/--') {
      return createDate(val)?.format('YYYY/MM/DD(ddd)') ?? defaultLabel
    },
    businessesPartnerName (val) {
      return val ? '企業担当者様' : '制作担当者'
    }
  },
  mixins: [jobPreviewCommon],
  props: {
    jobComment: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      isCompanyStaffAccess: (this.$route.name !== 'admPreview'),
      status: {
        isOpen: this.jobComment.isOpen
      },
      isEdit: false
    }
  },
  computed: {
    commentAreaId () {
      return this.jobComment.highlight
        ? 'comment-' + this.jobComment.target + '-' + this.jobComment.highlight.start
        : null
    },
    getClComment () {
      // 現在各コメント1件ずつの制限があるので確定で１件目を取得
      return this.jobComment.comments.filter(comment => comment.isCompanyStaff !== this.isCompanyStaffAccess)[0]
    },
    clCommentHonorific () {
      return (this.getClComment.author && !this.isCompanyStaffAccess) ? '様' : ''
    },
    getMyComment () {
      const myComment = this.jobComment.comments.filter(comment => comment.isCompanyStaff === this.isCompanyStaffAccess)
      if (!myComment.length) {
        this.$emit('addComment', this.jobComment)
        // emit後の値を再取得。現在各コメント1件ずつの制限があるので確定で１件目を取得
        return this.jobComment.comments.filter(comment => comment.isCompanyStaff === this.isCompanyStaffAccess)[0]
      }
      return myComment[0] // 現在各コメント1件ずつの制限があるので確定で１件目を取得
    },
    myCommentHonorific () {
      return (this.getMyComment.author && this.isCompanyStaffAccess) ? '様' : ''
    },
    isNothingComments () {
      return this.jobComment.comments.every(comment => {
        return !comment.isPending && // 未編集
        comment.comment === ''// コメントなし
      })
    }
  },
  watch: {
    jobComment () {
      this.setIsEdit()
    }
  },
  mounted () {
    this.setIsEdit()
  },
  methods: {
    async deleteComment () {
      // 削除権限チェック
      if (this.isCompanyStaffAccess && !this.jobComment.isCompanyStaff) {
        // 企業担当者は、企業担当者以外が作成したコメントを削除できない
        this.$toasted.global.error({ message: '相手側の作成したコメントであるため、削除できませんでした。' })
        return
      }

      // 双方未コメントで未だ編集されていない場合はダイアログなしで削除
      if (this.isNothingComments) {
        this.deleteCommentAction()
        return
      }

      const hasHighlight = !!this.jobComment.highlight

      this.$dialog.confirm(hasHighlight ? 'このコメントとハイライトを削除しますか？' : 'このコメントを削除しますか？')
        // はいを押下した時
        .then(() => {
          this.deleteCommentAction()
        })
        // いいえを押下した時（catch句を消すとコンソールに警告が出るから消さないでね！！）
        .catch(() => {})
    },
    deleteCommentAction () {
      // コメント削除
      this.$emit('deleteComment', this.jobComment)

      if (!this.jobComment.highlight) {
        this.$toasted.global.success({ message: 'コメントを削除しました。' })
        return // ハイライト情報がない場合は以降の処理不要
      }
      const element = document.getElementById(this.jobComment.target)
      if (element.tagName === 'IMG') {
        // 画像ハイライト削除
        this.$emit('deleteImgHighlight', element)
        this.$toasted.global.success({ message: 'ハイライトとコメントを削除しました。' })
        return
      }
      const highlightRange = this.getTargetRange(this.jobComment, element)
      if (!highlightRange) {
        this.$toasted.global.success({ message: 'コメントを削除しました。' })
        return
      }
      // 画面上のハイライトを削除
      this.$emit('deleteTextHighlight', highlightRange.endContainer.parentNode)
      this.$toasted.global.success({ message: 'ハイライトとコメントを削除しました。' })
    },
    toggle () {
      this.status.isOpen = !this.status.isOpen
      this.$emit('toggle', this.jobComment)
    },
    setIsEdit () {
      this.isEdit = !!this.getMyComment.isPending
    },
    changeEdit () {
      this.isEdit = !this.isEdit
      this.$emit('setLine')
    }
  }
}
</script>
